if (!customElements.get('haven-mobile-purchase')) {
  customElements.define(
    'haven-mobile-purchase',
    class HavenMobilePurchase extends HTMLElement {
      constructor() {
        super();
        this.mediaQuery = window.matchMedia('(max-width: 749px)');
        this.frameRequest = null;
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleViewportChange = this.handleViewportChange.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('[data-haven-sticky-submit]');
        this.label = this.querySelector('[data-haven-sticky-label]');
        this.price = this.querySelector('[data-haven-sticky-price]');
        this.mainPurchaseArea = this.productInfo?.querySelector('.product-form__buttons');
        this.mainSubmit = this.productInfo?.querySelector('#ProductSubmitButton-' + this.sectionId);
        this.footer = document.querySelector('footer');

        if (!this.productInfo || !this.button || !this.mainPurchaseArea || !this.mainSubmit) return;

        this.hidden = false;
        this.button.addEventListener('click', this.handleSubmit);
        this.mediaQuery.addEventListener('change', this.handleViewportChange);
        window.addEventListener('scroll', this.handleScroll, { passive: true });

        this.intersectionObserver = new IntersectionObserver(
          () => this.handleScroll(),
          { threshold: 0.01 }
        );
        this.intersectionObserver.observe(this.mainPurchaseArea);

        this.mutationObserver = new MutationObserver((mutations) => {
          if (mutations.some((mutation) => !this.contains(mutation.target))) {
            this.syncState();
          }
        });
        this.mutationObserver.observe(this.productInfo, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
        });

        this.syncState();
        this.handleScroll();
      }

      disconnectedCallback() {
        this.button?.removeEventListener('click', this.handleSubmit);
        this.mediaQuery?.removeEventListener('change', this.handleViewportChange);
        window.removeEventListener('scroll', this.handleScroll);
        this.intersectionObserver?.disconnect();
        this.mutationObserver?.disconnect();
        if (this.frameRequest !== null) {
          cancelAnimationFrame(this.frameRequest);
          this.frameRequest = null;
        }
        if (this.classList.contains('is-visible')) document.body.classList.remove('haven-mobile-buy-visible');
      }

      handleViewportChange() {
        this.handleScroll();
      }

      handleScroll() {
        if (this.frameRequest !== null) return;

        this.frameRequest = requestAnimationFrame(() => {
          this.frameRequest = null;
          this.updateFromScroll();
        });
      }

      updateFromScroll() {
        if (!this.mediaQuery.matches) {
          this.purchaseAreaPassed = false;
          this.footerInView = false;
          this.updateVisibility();
          return;
        }

        const purchaseRect = this.mainPurchaseArea?.getBoundingClientRect();
        const footerRect = this.footer?.getBoundingClientRect();

        this.purchaseAreaPassed = Boolean(purchaseRect && purchaseRect.bottom < 0);
        this.footerInView = Boolean(footerRect && footerRect.top < window.innerHeight && footerRect.bottom > 0);
        this.updateVisibility();
      }

      handleSubmit() {
        if (this.button.disabled || this.mainSubmit.disabled || this.mainSubmit.getAttribute('aria-disabled') === 'true') {
          return;
        }

        this.mainSubmit.click();
      }

      syncState() {
        this.mainSubmit = this.productInfo.querySelector('#ProductSubmitButton-' + this.sectionId);
        if (!this.mainSubmit) return;

        const currentPrice = this.productInfo.querySelector('#price-' + this.sectionId + ' .price');
        const priceSelector = currentPrice?.classList.contains('price--on-sale')
          ? '.price-item--sale'
          : '.price-item--regular';
        const priceText = currentPrice?.querySelector(priceSelector)?.textContent.trim();
        const mainLabel = this.mainSubmit.querySelector('span')?.textContent.trim();
        const unavailable =
          this.mainSubmit.disabled || this.mainSubmit.getAttribute('aria-disabled') === 'true';

        if (priceText) this.price.textContent = priceText;
        if (mainLabel) this.label.textContent = mainLabel;

        this.button.disabled = unavailable;
        this.button.classList.toggle('loading', this.mainSubmit.classList.contains('loading'));
        this.button.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      }

      updateVisibility() {
        const shouldShow = this.mediaQuery.matches && this.purchaseAreaPassed && !this.footerInView;
        this.classList.toggle('is-visible', shouldShow);
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        document.body.classList.toggle('haven-mobile-buy-visible', shouldShow);
      }
    }
  );
}
